import { createSupplierSeeds } from './data/suppliers.js';
import { createPurchaseSeeds } from './data/purchases.js';
import { createAsistenciaSeeds, createTurnoSeeds, createAsignacionesTurnoSeeds, createVacacionesSeeds, EMPLEADOS } from './data/rrhh.js';
import { applyPagination, matchesSearch, normalizeString, deepClone, sortPurchasesByDate } from './utils.js';

const turnosSeeds = createTurnoSeeds();

const mockState = {
  suppliers: createSupplierSeeds(),
  purchases: createPurchaseSeeds(),
  asistencias: createAsistenciaSeeds(),
  turnos: turnosSeeds,
  asignacionesTurno: createAsignacionesTurnoSeeds(turnosSeeds),
  vacaciones: createVacacionesSeeds(),
};

let supplierSequence = mockState.suppliers.length + 1;
let purchaseSequence = mockState.purchases.length + 1;
let attachmentSequence = 1;
let itemSequence = 1;
let asistenciaSequence = mockState.asistencias.length + 1;
let turnoSequence = mockState.turnos.length + 1;
let asignacionSequence = mockState.asignacionesTurno.length + 1;
let vacacionSequence = mockState.vacaciones.length + 1;

function nowIso() {
  return new Date().toISOString();
}

function getSupplierById(id) {
  return mockState.suppliers.find(item => item.id === id) || null;
}

function makeSupplierPayload(input) {
  const errors = [];
  if (!input.razonSocial || !input.razonSocial.trim()) {
    errors.push('La razón social es obligatoria.');
  }
  if (!input.cuit || !/^\d{2}-?\d{8}-?\d$/.test(input.cuit)) {
    errors.push('El CUIT debe tener 11 dígitos.');
  }
  if (input.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.email)) {
    errors.push('El correo electrónico tiene un formato inválido.');
  }
  if (input.telefono && input.telefono.length > 40) {
    errors.push('El teléfono es demasiado largo.');
  }
  if (errors.length) {
    return { errors };
  }
  return {
    razonSocial: input.razonSocial.trim(),
    cuit: input.cuit.replace(/[^\d]/g, '').replace(/(\d{2})(\d{8})(\d{1})/, '$1-$2-$3'),
    condicionIva: input.condicionIva || 'Consumidor Final',
    email: input.email ? input.email.trim() : '',
    telefono: input.telefono ? input.telefono.trim() : '',
    domicilio: {
      calle: input.domicilio?.calle || '',
      numero: input.domicilio?.numero || '',
      piso: input.domicilio?.piso || '',
      dpto: input.domicilio?.dpto || '',
      localidad: input.domicilio?.localidad || '',
      provincia: input.domicilio?.provincia || '',
      cp: input.domicilio?.cp || '',
    },
    observaciones: input.observaciones || '',
    activo: input.activo !== undefined ? Boolean(input.activo) : true,
  };
}

function handleSuppliersQuery(variables) {
  const { filter = {}, pagination } = variables || {};
  const { q, activo, ids } = filter;
  let rows = mockState.suppliers;
  if (Array.isArray(ids) && ids.length) {
    rows = rows.filter(item => ids.includes(item.id));
  }
  rows = rows.filter(item => {
    const matchesText = matchesSearch(item, ['razonSocial', 'cuit'], q);
    const matchesStatus = activo === undefined ? true : item.activo === Boolean(activo);
    return matchesText && matchesStatus;
  });
  const sorted = [...rows].sort((a, b) => normalizeString(a.razonSocial).localeCompare(normalizeString(b.razonSocial)));
  const { items, pageInfo } = applyPagination(sorted, pagination);
  return {
    suppliers: {
      items: items.map(item => deepClone(item)),
      pageInfo,
    },
  };
}

function handleSupplierQuery(variables) {
  const supplier = getSupplierById(variables?.id);
  if (!supplier) {
    throw new Error('Proveedor no encontrado');
  }
  return { supplier: deepClone(supplier) };
}

function handleCreateSupplier(variables) {
  const { input } = variables || {};
  const payload = makeSupplierPayload(input || {});
  if (payload.errors) {
    const errorMessage = payload.errors.join(' ');
    const error = new Error(errorMessage);
    error.extensions = { code: 'BAD_USER_INPUT' };
    throw error;
  }
  const id = `prov-${supplierSequence}`;
  supplierSequence += 1;
  const supplier = {
    id,
    ...payload,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  };
  mockState.suppliers.push(supplier);
  return { createSupplier: deepClone(supplier) };
}

function handleUpdateSupplier(variables) {
  const { id, input } = variables || {};
  const index = mockState.suppliers.findIndex(item => item.id === id);
  if (index === -1) {
    throw new Error('Proveedor no encontrado');
  }
  const payload = makeSupplierPayload({ ...mockState.suppliers[index], ...input });
  if (payload.errors) {
    const errorMessage = payload.errors.join(' ');
    const error = new Error(errorMessage);
    error.extensions = { code: 'BAD_USER_INPUT' };
    throw error;
  }
  const updated = {
    ...mockState.suppliers[index],
    ...payload,
    activo: input?.activo ?? mockState.suppliers[index].activo,
    updatedAt: nowIso(),
  };
  mockState.suppliers[index] = updated;
  return { updateSupplier: deepClone(updated) };
}

function handleToggleSupplierActive(variables) {
  const { id } = variables || {};
  const supplier = getSupplierById(id);
  if (!supplier) {
    throw new Error('Proveedor no encontrado');
  }
  supplier.activo = !supplier.activo;
  supplier.updatedAt = nowIso();
  return { toggleSupplierActive: deepClone(supplier) };
}

function normalizeNumber(value) {
  if (value === null || value === undefined) {
    return 0;
  }
  const parsed = typeof value === 'string' ? value.replace(/[^0-9,.-]/g, '') : value;
  const normalized = typeof parsed === 'string' ? parsed.replace(/\./g, '').replace(',', '.') : parsed;
  const result = Number(normalized);
  return Number.isFinite(result) ? result : 0;
}

function makeImportes(input = {}) {
  const keys = ['neto', 'iva21', 'iva105', 'iva27', 'exento', 'noGravado', 'percepIva', 'percepIibb', 'otros', 'total'];
  const importes = {};
  keys.forEach(key => {
    importes[key] = normalizeNumber(input[key] ?? 0);
  });
  return importes;
}

function makeItems(items = []) {
  return items.map(item => ({
    id: item.id || `item-${itemSequence++}`,
    codigo: item.codigo || '',
    descripcion: item.descripcion || '',
    cantidad: Number(item.cantidad) || 0,
    precio: normalizeNumber(item.precio),
    alicuotaIva: Number(item.alicuotaIva) || 0,
    subtotal: normalizeNumber(item.subtotal ?? item.cantidad * item.precio),
  }));
}

function makeAttachments(files = []) {
  return files.map(file => ({
    id: file.id || `att-${attachmentSequence++}`,
    filename: file.filename || file.name || 'adjunto',
    url: file.url || `/uploads/${file.filename || file.name || 'adjunto'}`,
    mimeType: file.mimeType || file.type || 'application/octet-stream',
    size: Number(file.size) || 0,
  }));
}

function makePurchasePayload(input = {}) {
  const supplier = getSupplierById(input.supplierId);
  if (!supplier) {
    const error = new Error('El proveedor seleccionado no existe.');
    error.extensions = { code: 'BAD_USER_INPUT' };
    throw error;
  }
  const importes = makeImportes(input.importes);
  const items = makeItems(input.items);
  const attachments = makeAttachments(input.adjuntos || input.attachments);
  return {
    supplierId: supplier.id,
    tipo: input.tipo || 'Factura A',
    puntoVenta: input.puntoVenta || '0001',
    numero: input.numero || '',
    fecha: input.fecha || nowIso().slice(0, 10),
    cae: input.cae || '',
    caeVto: input.caeVto || '',
    moneda: input.moneda || 'ARS',
    condicionCompra: input.condicionCompra || 'Contado',
    importes,
    items,
    adjuntos: attachments,
    observaciones: input.observaciones || '',
    estado: input.estado || 'Registrada',
  };
}

function buildPurchaseResponse(purchase) {
  const supplier = getSupplierById(purchase.supplierId);
  return {
    ...purchase,
    supplier: supplier ? deepClone(supplier) : null,
  };
}

function handlePurchasesQuery(variables) {
  const { filter = {}, pagination } = variables || {};
  let rows = mockState.purchases;
  if (filter.supplierId) {
    rows = rows.filter(item => item.supplierId === filter.supplierId);
  }
  if (filter.tipo) {
    rows = rows.filter(item => normalizeString(item.tipo) === normalizeString(filter.tipo));
  }
  if (filter.q) {
    const query = normalizeString(filter.q);
    rows = rows.filter(item => {
      const supplier = getSupplierById(item.supplierId);
      return (
        normalizeString(item.numero).includes(query) ||
        normalizeString(item.puntoVenta).includes(query) ||
        (supplier && normalizeString(supplier.razonSocial).includes(query))
      );
    });
  }
  if (filter.fechaDesde) {
    const from = new Date(filter.fechaDesde).getTime();
    rows = rows.filter(item => new Date(item.fecha).getTime() >= from);
  }
  if (filter.fechaHasta) {
    const to = new Date(filter.fechaHasta).getTime();
    rows = rows.filter(item => new Date(item.fecha).getTime() <= to);
  }
  const sorted = sortPurchasesByDate(rows);
  const { items, pageInfo } = applyPagination(sorted, pagination);
  return {
    purchases: {
      items: items.map(item => deepClone(buildPurchaseResponse(item))),
      pageInfo,
    },
  };
}

function handlePurchaseQuery(variables) {
  const purchase = mockState.purchases.find(item => item.id === variables?.id);
  if (!purchase) {
    throw new Error('Compra no encontrada');
  }
  return { purchase: deepClone(buildPurchaseResponse(purchase)) };
}

function handleCreatePurchase(variables) {
  const { input } = variables || {};
  const payload = makePurchasePayload(input);
  const id = `comp-${purchaseSequence}`;
  purchaseSequence += 1;
  const purchase = {
    id,
    ...payload,
    items: payload.items.map(item => ({ ...item, id: item.id || `item-${itemSequence++}` })),
    adjuntos: payload.adjuntos.map(file => ({ ...file, id: file.id || `att-${attachmentSequence++}` })),
    createdAt: nowIso(),
    updatedAt: nowIso(),
  };
  mockState.purchases.push(purchase);
  return { createPurchase: deepClone(buildPurchaseResponse(purchase)) };
}

function handleUpdatePurchase(variables) {
  const { id, input } = variables || {};
  const index = mockState.purchases.findIndex(item => item.id === id);
  if (index === -1) {
    throw new Error('Compra no encontrada');
  }
  const payload = makePurchasePayload({ ...mockState.purchases[index], ...input });
  const updated = {
    ...mockState.purchases[index],
    ...payload,
    items: payload.items.map(item => ({ ...item, id: item.id || `item-${itemSequence++}` })),
    adjuntos: payload.adjuntos.map(file => ({ ...file, id: file.id || `att-${attachmentSequence++}` })),
    updatedAt: nowIso(),
  };
  mockState.purchases[index] = updated;
  return { updatePurchase: deepClone(buildPurchaseResponse(updated)) };
}

function handleAttachPurchaseFiles(variables) {
  const { id, filesMeta } = variables || {};
  const purchase = mockState.purchases.find(item => item.id === id);
  if (!purchase) {
    throw new Error('Compra no encontrada');
  }
  const newFiles = makeAttachments(filesMeta);
  purchase.adjuntos = [...purchase.adjuntos, ...newFiles];
  purchase.updatedAt = nowIso();
  return { attachPurchaseFiles: deepClone(buildPurchaseResponse(purchase)) };
}

function handleDeletePurchase(variables) {
  const { id } = variables || {};
  const index = mockState.purchases.findIndex(item => item.id === id);
  if (index === -1) {
    throw new Error('Compra no encontrada');
  }
  mockState.purchases.splice(index, 1);
  return { deletePurchase: { success: true } };
}

function sortCollection(rows, order, defaultField, fallbackDirection = 'DESC') {
  const field = order?.field || defaultField;
  const direction = (order?.direction || fallbackDirection).toUpperCase() === 'ASC' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const valueA = a[field];
    const valueB = b[field];
    if (valueA === valueB) return 0;
    if (valueA === undefined || valueA === null) return -direction;
    if (valueB === undefined || valueB === null) return direction;

    const dateA = Date.parse(valueA);
    const dateB = Date.parse(valueB);
    if (!Number.isNaN(dateA) && !Number.isNaN(dateB)) {
      return (dateA - dateB) * direction;
    }

    if (typeof valueA === 'number' && typeof valueB === 'number') {
      return (valueA - valueB) * direction;
    }

    return normalizeString(valueA).localeCompare(normalizeString(valueB)) * direction;
  });
}

function computeHoras(asistencia) {
  if (!asistencia.checkIn || !asistencia.checkOut || !asistencia.fecha) {
    return 0;
  }
  const start = new Date(`${asistencia.fecha}T${asistencia.checkIn}`);
  const end = new Date(`${asistencia.fecha}T${asistencia.checkOut}`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return 0;
  }
  const diff = (end - start) / (1000 * 60 * 60);
  return diff > 0 ? diff : 0;
}

function findEmpleadoMeta(id) {
  const empleado = EMPLEADOS.find(item => item.id === Number(id));
  return {
    empleadoId: Number(id),
    empleadoNombre: empleado?.nombre || `Empleado ${id}`,
    sucursalId: empleado?.sucursalId || null,
    sucursalNombre: empleado?.sucursalNombre || null,
  };
}

function resolveSucursalNombre(sucursalId, fallback) {
  if (!sucursalId) return fallback || null;
  const empleado = EMPLEADOS.find(item => item.sucursalId === Number(sucursalId));
  return empleado?.sucursalNombre || fallback || `Sucursal ${sucursalId}`;
}

function getAsistenciaById(id) {
  return mockState.asistencias.find(item => Number(item.id) === Number(id)) || null;
}

function makeAsistenciaPayload(input = {}, existing = {}) {
  const errors = [];
  const empleadoId = Number(input.empleadoId ?? existing.empleadoId);
  if (!empleadoId) {
    errors.push('El empleado es obligatorio.');
  }
  const fecha = input.fecha ?? existing.fecha;
  if (!fecha) {
    errors.push('La fecha es obligatoria.');
  }
  const checkIn = input.checkIn ?? existing.checkIn ?? null;
  const checkOut = input.checkOut ?? existing.checkOut ?? null;
  if (checkIn && checkOut && checkOut < checkIn) {
    errors.push('El check-out debe ser posterior al check-in.');
  }
  if (errors.length) {
    return { errors };
  }
  const meta = findEmpleadoMeta(empleadoId);
  const sucursalId = input.sucursalId !== undefined ? Number(input.sucursalId) : existing.sucursalId ?? meta.sucursalId ?? null;
  return {
    empleadoId,
    empleadoNombre: meta.empleadoNombre,
    sucursalId,
    sucursalNombre: resolveSucursalNombre(sucursalId, existing.sucursalNombre || meta.sucursalNombre),
    fecha,
    checkIn: checkIn || null,
    checkOut: checkOut || null,
    metodo: input.metodo || existing.metodo || 'Manual',
    lat: input.lat !== undefined ? Number(input.lat) : existing.lat ?? null,
    lng: input.lng !== undefined ? Number(input.lng) : existing.lng ?? null,
    estado: input.estado || existing.estado || 'EnCurso',
    notas: input.notas ?? existing.notas ?? '',
  };
}

function handleAsistenciasQuery(variables = {}) {
  const { filters = {}, pagination, order } = variables;
  let rows = mockState.asistencias;
  if (filters.empleadoId) {
    rows = rows.filter(item => Number(item.empleadoId) === Number(filters.empleadoId));
  }
  if (filters.sucursalId) {
    rows = rows.filter(item => Number(item.sucursalId) === Number(filters.sucursalId));
  }
  if (filters.estado) {
    rows = rows.filter(item => item.estado === filters.estado);
  }
  if (filters.fechaDesde) {
    rows = rows.filter(item => item.fecha >= filters.fechaDesde);
  }
  if (filters.fechaHasta) {
    rows = rows.filter(item => item.fecha <= filters.fechaHasta);
  }
  const sorted = sortCollection(rows, order, 'fecha', 'DESC');
  const { items, pageInfo } = applyPagination(sorted, pagination);
  const summary = rows.reduce((acc, asistencia) => {
    return {
      totalHoras: acc.totalHoras + computeHoras(asistencia),
      llegadasTarde: acc.llegadasTarde + (asistencia.estado === 'Tarde' ? 1 : 0),
      ausencias: acc.ausencias + (asistencia.estado === 'Ausente' ? 1 : 0),
    };
  }, { totalHoras: 0, llegadasTarde: 0, ausencias: 0 });
  return {
    asistencias: {
      total: rows.length,
      summary,
      pageInfo,
      items: items.map(item => deepClone(item)),
    },
  };
}

function handleAsistenciaById(variables = {}) {
  const asistencia = getAsistenciaById(variables.id);
  if (!asistencia) {
    throw new Error('Asistencia no encontrada');
  }
  return { asistencia: deepClone(asistencia) };
}

function handleCreateAsistencia(variables = {}) {
  const payload = makeAsistenciaPayload(variables.input || {});
  if (payload.errors) {
    const error = new Error(payload.errors.join(' '));
    error.extensions = { code: 'BAD_USER_INPUT' };
    throw error;
  }
  const asistencia = {
    id: asistenciaSequence,
    ...payload,
  };
  asistenciaSequence += 1;
  mockState.asistencias.push(asistencia);
  return { asistenciaCreate: deepClone(asistencia) };
}

function handleUpdateAsistencia(variables = {}) {
  const index = mockState.asistencias.findIndex(item => Number(item.id) === Number(variables.id));
  if (index === -1) {
    throw new Error('Asistencia no encontrada');
  }
  const payload = makeAsistenciaPayload(variables.input || {}, mockState.asistencias[index]);
  if (payload.errors) {
    const error = new Error(payload.errors.join(' '));
    error.extensions = { code: 'BAD_USER_INPUT' };
    throw error;
  }
  const updated = { ...mockState.asistencias[index], ...payload };
  mockState.asistencias[index] = updated;
  return { asistenciaUpdate: deepClone(updated) };
}

function handleDeleteAsistencia(variables = {}) {
  const index = mockState.asistencias.findIndex(item => Number(item.id) === Number(variables.id));
  if (index === -1) {
    throw new Error('Asistencia no encontrada');
  }
  mockState.asistencias.splice(index, 1);
  return { asistenciaDelete: true };
}

function getTurnoById(id) {
  return mockState.turnos.find(item => Number(item.id) === Number(id)) || null;
}

function makeTurnoPayload(input = {}, existing = {}) {
  const errors = [];
  const nombre = input.nombre ?? existing.nombre;
  if (!nombre || !nombre.trim()) {
    errors.push('El nombre del turno es obligatorio.');
  }
  const horaEntrada = input.horaEntrada ?? existing.horaEntrada;
  const horaSalida = input.horaSalida ?? existing.horaSalida;
  if (!horaEntrada) {
    errors.push('La hora de entrada es obligatoria.');
  }
  if (!horaSalida) {
    errors.push('La hora de salida es obligatoria.');
  }
  if (horaEntrada && horaSalida && horaEntrada >= horaSalida) {
    errors.push('La hora de salida debe ser posterior a la de entrada.');
  }
  if (errors.length) {
    return { errors };
  }
  const sucursalId = input.sucursalId !== undefined ? Number(input.sucursalId) : existing.sucursalId ?? null;
  return {
    nombre: nombre.trim(),
    descripcion: input.descripcion ?? existing.descripcion ?? '',
    horaEntrada,
    horaSalida,
    descansoMinutos: Number(input.descansoMinutos ?? existing.descansoMinutos ?? 0),
    aplicaFinesDeSemana: input.aplicaFinesDeSemana !== undefined ? Boolean(input.aplicaFinesDeSemana) : Boolean(existing.aplicaFinesDeSemana),
    sucursalId,
    sucursalNombre: resolveSucursalNombre(sucursalId, existing.sucursalNombre),
    activo: input.activo !== undefined ? Boolean(input.activo) : existing.activo !== undefined ? Boolean(existing.activo) : true,
  };
}

function handleTurnosQuery(variables = {}) {
  const { filters = {}, pagination, order } = variables;
  let rows = mockState.turnos;
  if (filters.sucursalId) {
    rows = rows.filter(item => Number(item.sucursalId) === Number(filters.sucursalId));
  }
  if (filters.activo !== undefined && filters.activo !== null) {
    rows = rows.filter(item => Boolean(item.activo) === Boolean(filters.activo));
  }
  if (filters.search) {
    rows = rows.filter(item => matchesSearch(item, ['nombre', 'descripcion'], filters.search));
  }
  const sorted = sortCollection(rows, order, 'creadoEn', 'DESC');
  const { items, pageInfo } = applyPagination(sorted, pagination);
  return {
    turnos: {
      total: rows.length,
      pageInfo,
      items: items.map(item => deepClone(item)),
    },
  };
}

function handleTurnoById(variables = {}) {
  const turno = getTurnoById(variables.id);
  if (!turno) {
    throw new Error('Turno no encontrado');
  }
  return { turno: deepClone(turno) };
}

function handleCreateTurno(variables = {}) {
  const payload = makeTurnoPayload(variables.input || {});
  if (payload.errors) {
    const error = new Error(payload.errors.join(' '));
    error.extensions = { code: 'BAD_USER_INPUT' };
    throw error;
  }
  const turno = {
    id: turnoSequence,
    ...payload,
    creadoEn: nowIso(),
  };
  turnoSequence += 1;
  mockState.turnos.push(turno);
  return { turnoCreate: deepClone(turno) };
}

function handleUpdateTurno(variables = {}) {
  const index = mockState.turnos.findIndex(item => Number(item.id) === Number(variables.id));
  if (index === -1) {
    throw new Error('Turno no encontrado');
  }
  const payload = makeTurnoPayload(variables.input || {}, mockState.turnos[index]);
  if (payload.errors) {
    const error = new Error(payload.errors.join(' '));
    error.extensions = { code: 'BAD_USER_INPUT' };
    throw error;
  }
  const updated = { ...mockState.turnos[index], ...payload };
  mockState.turnos[index] = updated;
  return { turnoUpdate: deepClone(updated) };
}

function handleDeleteTurno(variables = {}) {
  const index = mockState.turnos.findIndex(item => Number(item.id) === Number(variables.id));
  if (index === -1) {
    throw new Error('Turno no encontrado');
  }
  const turnoId = mockState.turnos[index].id;
  mockState.turnos.splice(index, 1);
  mockState.asignacionesTurno = mockState.asignacionesTurno.filter(item => Number(item.turnoId) !== Number(turnoId));
  return { turnoDelete: true };
}

function getAsignacionById(id) {
  return mockState.asignacionesTurno.find(item => Number(item.id) === Number(id)) || null;
}

function makeAsignacionPayload(input = {}, existing = {}) {
  const errors = [];
  const empleadoMeta = findEmpleadoMeta(input.empleadoId ?? existing.empleadoId);
  if (!empleadoMeta.empleadoId) {
    errors.push('El empleado es obligatorio.');
  }
  const turno = getTurnoById(input.turnoId ?? existing.turnoId);
  if (!turno) {
    errors.push('El turno seleccionado no existe.');
  }
  const desde = input.desde ?? existing.desde;
  if (!desde) {
    errors.push('La fecha de inicio es obligatoria.');
  }
  const hasta = input.hasta ?? existing.hasta ?? null;
  if (desde && hasta && hasta < desde) {
    errors.push('La fecha de fin debe ser posterior a la de inicio.');
  }
  if (errors.length) {
    return { errors };
  }
  const sucursalId = input.sucursalId !== undefined ? Number(input.sucursalId) : existing.sucursalId ?? turno?.sucursalId ?? empleadoMeta.sucursalId ?? null;
  return {
    turnoId: turno?.id ?? Number(input.turnoId),
    turnoNombre: turno?.nombre || existing.turnoNombre || 'Turno',
    empleadoId: empleadoMeta.empleadoId,
    empleadoNombre: empleadoMeta.empleadoNombre,
    desde,
    hasta,
    estado: input.estado || existing.estado || 'Activa',
    sucursalId,
    sucursalNombre: resolveSucursalNombre(sucursalId, existing.sucursalNombre || turno?.sucursalNombre || empleadoMeta.sucursalNombre),
  };
}

function handleAsignacionesTurnoQuery(variables = {}) {
  const { filters = {}, pagination, order } = variables;
  let rows = mockState.asignacionesTurno;
  if (filters.empleadoId) {
    rows = rows.filter(item => Number(item.empleadoId) === Number(filters.empleadoId));
  }
  if (filters.turnoId) {
    rows = rows.filter(item => Number(item.turnoId) === Number(filters.turnoId));
  }
  if (filters.sucursalId) {
    rows = rows.filter(item => Number(item.sucursalId) === Number(filters.sucursalId));
  }
  if (filters.estado) {
    rows = rows.filter(item => item.estado === filters.estado);
  }
  if (filters.desde) {
    rows = rows.filter(item => item.desde >= filters.desde);
  }
  if (filters.hasta) {
    rows = rows.filter(item => !item.hasta || item.hasta <= filters.hasta);
  }
  const sorted = sortCollection(rows, order, 'desde', 'DESC');
  const { items, pageInfo } = applyPagination(sorted, pagination);
  return {
    asignacionesTurno: {
      total: rows.length,
      pageInfo,
      items: items.map(item => deepClone(item)),
    },
  };
}

function handleAsignacionTurnoById(variables = {}) {
  const asignacion = getAsignacionById(variables.id);
  if (!asignacion) {
    throw new Error('Asignación no encontrada');
  }
  return { asignacionTurno: deepClone(asignacion) };
}

function handleCreateAsignacionTurno(variables = {}) {
  const payload = makeAsignacionPayload(variables.input || {});
  if (payload.errors) {
    const error = new Error(payload.errors.join(' '));
    error.extensions = { code: 'BAD_USER_INPUT' };
    throw error;
  }
  const asignacion = {
    id: asignacionSequence,
    ...payload,
  };
  asignacionSequence += 1;
  mockState.asignacionesTurno.push(asignacion);
  return { asignacionTurnoCreate: deepClone(asignacion) };
}

function handleUpdateAsignacionTurno(variables = {}) {
  const index = mockState.asignacionesTurno.findIndex(item => Number(item.id) === Number(variables.id));
  if (index === -1) {
    throw new Error('Asignación no encontrada');
  }
  const payload = makeAsignacionPayload(variables.input || {}, mockState.asignacionesTurno[index]);
  if (payload.errors) {
    const error = new Error(payload.errors.join(' '));
    error.extensions = { code: 'BAD_USER_INPUT' };
    throw error;
  }
  const updated = { ...mockState.asignacionesTurno[index], ...payload };
  mockState.asignacionesTurno[index] = updated;
  return { asignacionTurnoUpdate: deepClone(updated) };
}

function handleDeleteAsignacionTurno(variables = {}) {
  const index = mockState.asignacionesTurno.findIndex(item => Number(item.id) === Number(variables.id));
  if (index === -1) {
    throw new Error('Asignación no encontrada');
  }
  mockState.asignacionesTurno.splice(index, 1);
  return { asignacionTurnoDelete: true };
}

function getVacacionById(id) {
  return mockState.vacaciones.find(item => Number(item.id) === Number(id)) || null;
}

function makeVacacionPayload(input = {}, existing = {}) {
  const errors = [];
  const empleadoMeta = findEmpleadoMeta(input.empleadoId ?? existing.empleadoId);
  if (!empleadoMeta.empleadoId) {
    errors.push('El empleado es obligatorio.');
  }
  const periodo = input.periodo ?? existing.periodo;
  if (!periodo) {
    errors.push('El periodo es obligatorio.');
  }
  const desde = input.desde ?? existing.desde;
  const hasta = input.hasta ?? existing.hasta;
  if (!desde || !hasta) {
    errors.push('Debe informar fechas de inicio y fin.');
  } else if (desde > hasta) {
    errors.push('La fecha de fin debe ser posterior a la de inicio.');
  }
  if (errors.length) {
    return { errors };
  }
  const diasTomados = Number(input.diasTomados ?? calculateDiasTomados(desde, hasta, existing.diasTomados));
  const diasRestantes = Number(input.diasRestantes ?? existing.diasRestantes ?? 0);
  const sucursalId = input.sucursalId !== undefined ? Number(input.sucursalId) : existing.sucursalId ?? empleadoMeta.sucursalId ?? null;
  return {
    empleadoId: empleadoMeta.empleadoId,
    empleadoNombre: empleadoMeta.empleadoNombre,
    periodo,
    desde,
    hasta,
    diasTomados,
    diasRestantes,
    estado: input.estado || existing.estado || 'Solicitada',
    aprobadorId: input.aprobadorId !== undefined ? (input.aprobadorId ? Number(input.aprobadorId) : null) : existing.aprobadorId ?? null,
    notas: input.notas ?? existing.notas ?? '',
    sucursalId,
    sucursalNombre: resolveSucursalNombre(sucursalId, existing.sucursalNombre || empleadoMeta.sucursalNombre),
  };
}

function calculateDiasTomados(desde, hasta, fallback = 0) {
  if (!desde || !hasta) {
    return Number(fallback) || 0;
  }
  const start = new Date(desde);
  const end = new Date(hasta);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return Number(fallback) || 0;
  }
  return Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1);
}

function handleVacacionesQuery(variables = {}) {
  const { filters = {}, pagination, order } = variables;
  let rows = mockState.vacaciones;
  if (filters.periodo) {
    rows = rows.filter(item => item.periodo === filters.periodo);
  }
  if (filters.estado) {
    rows = rows.filter(item => item.estado === filters.estado);
  }
  if (filters.sucursalId) {
    rows = rows.filter(item => Number(item.sucursalId) === Number(filters.sucursalId));
  }
  if (filters.empleadoId) {
    rows = rows.filter(item => Number(item.empleadoId) === Number(filters.empleadoId));
  }
  const sorted = sortCollection(rows, order, 'desde', 'DESC');
  const { items, pageInfo } = applyPagination(sorted, pagination);
  return {
    vacaciones: {
      total: rows.length,
      pageInfo,
      items: items.map(item => deepClone(item)),
    },
  };
}

function handleVacacionById(variables = {}) {
  const vacacion = getVacacionById(variables.id);
  if (!vacacion) {
    throw new Error('Solicitud de vacaciones no encontrada');
  }
  return { vacacion: deepClone(vacacion) };
}

function handleCreateVacacion(variables = {}) {
  const payload = makeVacacionPayload(variables.input || {});
  if (payload.errors) {
    const error = new Error(payload.errors.join(' '));
    error.extensions = { code: 'BAD_USER_INPUT' };
    throw error;
  }
  const vacacion = {
    id: vacacionSequence,
    ...payload,
  };
  vacacionSequence += 1;
  mockState.vacaciones.push(vacacion);
  return { vacacionCreate: deepClone(vacacion) };
}

function handleUpdateVacacion(variables = {}) {
  const index = mockState.vacaciones.findIndex(item => Number(item.id) === Number(variables.id));
  if (index === -1) {
    throw new Error('Solicitud de vacaciones no encontrada');
  }
  const payload = makeVacacionPayload(variables.input || {}, mockState.vacaciones[index]);
  if (payload.errors) {
    const error = new Error(payload.errors.join(' '));
    error.extensions = { code: 'BAD_USER_INPUT' };
    throw error;
  }
  const updated = { ...mockState.vacaciones[index], ...payload };
  mockState.vacaciones[index] = updated;
  return { vacacionUpdate: deepClone(updated) };
}

function handleDeleteVacacion(variables = {}) {
  const index = mockState.vacaciones.findIndex(item => Number(item.id) === Number(variables.id));
  if (index === -1) {
    throw new Error('Solicitud de vacaciones no encontrada');
  }
  mockState.vacaciones.splice(index, 1);
  return { vacacionDelete: true };
}

function updateVacacionEstado(id, estado, extras = {}) {
  const index = mockState.vacaciones.findIndex(item => Number(item.id) === Number(id));
  if (index === -1) {
    throw new Error('Solicitud de vacaciones no encontrada');
  }
  const vacacion = {
    ...mockState.vacaciones[index],
    estado,
    ...extras,
  };
  mockState.vacaciones[index] = vacacion;
  return vacacion;
}

function handleVacacionApprove(variables = {}) {
  const vacacion = updateVacacionEstado(variables.id, 'Aprobada', {
    aprobadorId: variables.aprobadorId ? Number(variables.aprobadorId) : 999,
    aprobadorNombre: 'RRHH Manager',
  });
  return { vacacionApprove: deepClone(vacacion) };
}

function handleVacacionReject(variables = {}) {
  const vacacion = updateVacacionEstado(variables.id, 'Rechazada', {
    aprobadorId: variables.aprobadorId ? Number(variables.aprobadorId) : 999,
    aprobadorNombre: 'RRHH Manager',
    notas: variables.notas || 'Rechazo registrado desde mock.',
  });
  return { vacacionReject: deepClone(vacacion) };
}

function handleVacacionSetGoce(variables = {}) {
  const vacacion = updateVacacionEstado(variables.id, 'Goce');
  return { vacacionSetGoce: deepClone(vacacion) };
}

const resolvers = {
  Suppliers: handleSuppliersQuery,
  Supplier: handleSupplierQuery,
  CreateSupplier: handleCreateSupplier,
  UpdateSupplier: handleUpdateSupplier,
  ToggleSupplierActive: handleToggleSupplierActive,
  Purchases: handlePurchasesQuery,
  Purchase: handlePurchaseQuery,
  CreatePurchase: handleCreatePurchase,
  UpdatePurchase: handleUpdatePurchase,
  AttachPurchaseFiles: handleAttachPurchaseFiles,
  DeletePurchase: handleDeletePurchase,
  AsistenciasList: handleAsistenciasQuery,
  AsistenciaById: handleAsistenciaById,
  AsistenciaCreate: handleCreateAsistencia,
  AsistenciaUpdate: handleUpdateAsistencia,
  AsistenciaDelete: handleDeleteAsistencia,
  TurnosList: handleTurnosQuery,
  TurnoById: handleTurnoById,
  TurnoCreate: handleCreateTurno,
  TurnoUpdate: handleUpdateTurno,
  TurnoDelete: handleDeleteTurno,
  AsignacionesTurnosList: handleAsignacionesTurnoQuery,
  AsignacionTurnoById: handleAsignacionTurnoById,
  AsignacionTurnoCreate: handleCreateAsignacionTurno,
  AsignacionTurnoUpdate: handleUpdateAsignacionTurno,
  AsignacionTurnoDelete: handleDeleteAsignacionTurno,
  VacacionesList: handleVacacionesQuery,
  VacacionById: handleVacacionById,
  VacacionCreate: handleCreateVacacion,
  VacacionUpdate: handleUpdateVacacion,
  VacacionDelete: handleDeleteVacacion,
  VacacionApprove: handleVacacionApprove,
  VacacionReject: handleVacacionReject,
  VacacionSetGoce: handleVacacionSetGoce,
};

export function createMockFetch() {
  return async function mockFetch(_url, options = {}) {
    const body = options.body ? JSON.parse(options.body) : {};
    const { operationName, variables } = body;
    const name = operationName || extractOperationName(body.query);

    if (!name || !resolvers[name]) {
      return new Response(
        JSON.stringify({ errors: [{ message: `Operación no soportada: ${name}` }] }),
        { status: 200, headers: { 'Content-Type': 'application/json' } },
      );
    }

    try {
      const data = resolvers[name](variables || {});
      return new Response(JSON.stringify({ data }), {
        status: 200,
        headers: { 'Content-Type': 'application/json' },
      });
    } catch (error) {
      return new Response(
        JSON.stringify({
          data: null,
          errors: [
            {
              message: error.message,
              extensions: error.extensions || { code: 'INTERNAL_SERVER_ERROR' },
            },
          ],
        }),
        { status: 200, headers: { 'Content-Type': 'application/json' } },
      );
    }
  };
}

function extractOperationName(query) {
  if (!query) {
    return null;
  }
  const match = /(?:mutation|query)\s+(\w+)/.exec(query);
  return match ? match[1] : null;
}

export function resetMockState() {
  mockState.suppliers = createSupplierSeeds();
  mockState.purchases = createPurchaseSeeds();
  mockState.asistencias = createAsistenciaSeeds();
  const turnosSeed = createTurnoSeeds();
  mockState.turnos = turnosSeed;
  mockState.asignacionesTurno = createAsignacionesTurnoSeeds(turnosSeed);
  mockState.vacaciones = createVacacionesSeeds();
  supplierSequence = mockState.suppliers.length + 1;
  purchaseSequence = mockState.purchases.length + 1;
  attachmentSequence = 1;
  itemSequence = 1;
  asistenciaSequence = mockState.asistencias.length + 1;
  turnoSequence = mockState.turnos.length + 1;
  asignacionSequence = mockState.asignacionesTurno.length + 1;
  vacacionSequence = mockState.vacaciones.length + 1;
}

